import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ExternalLink, BookOpen } from 'lucide-react';

const CONTENT = {
  en: {
    title: 'Independent Adrenal Insufficiency Resources',
    intro:
      'For medical information, emergency preparation resources and patient support, consult qualified healthcare professionals and recognized adrenal-disease organizations. The following external resources are provided for convenience.',
    externalLabel: 'opens an external site',
    disclaimer:
      'External resource notice: MyGuardPod does not create, control or replace the medical information published by these organizations. Resource content and availability may change. These links do not constitute medical advice or imply clinical certification of MyGuardPod products. Always follow your healthcare professional\u2019s instructions and personal emergency plan.',
    cards: [
      {
        org: 'National Adrenal Diseases Foundation \u2014 NADF',
        region: 'United States \u00b7 English, with selected Spanish resources',
        desc:
          'Patient resources concerning adrenal insufficiency, emergency preparedness, emergency cards, injection resources, support and educational materials.',
        cta: 'Visit NADF Patient Resources',
        url: 'https://www.nadf.us/patient-resources',
      },
      {
        org: 'The Canadian Addison Society / La Soci\u00e9t\u00e9 canadienne d\u2019Addison',
        region: 'Canada \u00b7 English and French',
        desc:
          'Canadian patient resources, including emergency documents, medical information cards, travel resources and materials for patients and emergency responders.',
        cta: 'Visit Canadian Addison Society Resources',
        url: 'https://addisonsociety.ca/resources/',
      },
    ],
  },
  fr: {
    title: 'Ressources externes sur l\u2019insuffisance surr\u00e9nalienne',
    intro:
      'Pour obtenir de l\u2019information m\u00e9dicale, des ressources de pr\u00e9paration aux urgences et du soutien aux patients, consultez des professionnels de la sant\u00e9 qualifi\u00e9s et des organismes reconnus sp\u00e9cialis\u00e9s dans les maladies surr\u00e9naliennes. Les ressources externes suivantes sont fournies \u00e0 titre pratique.',
    externalLabel: 'ouvre un site externe',
    disclaimer:
      'Avis concernant les ressources externes\u00a0: MyGuardPod ne cr\u00e9e pas, ne contr\u00f4le pas et ne remplace pas l\u2019information m\u00e9dicale publi\u00e9e par ces organismes. Le contenu et la disponibilit\u00e9 des ressources peuvent changer. Ces liens ne constituent pas des conseils m\u00e9dicaux et n\u2019impliquent aucune certification clinique des produits MyGuardPod. Respectez toujours les instructions de votre professionnel de la sant\u00e9 et votre plan d\u2019urgence personnel.',
    cards: [
      {
        org: 'National Adrenal Diseases Foundation \u2014 NADF',
        region: '\u00c9tats-Unis \u00b7 anglais, avec certaines ressources en espagnol',
        desc:
          'Ressources destin\u00e9es aux patients concernant l\u2019insuffisance surr\u00e9nalienne\u00a0: pr\u00e9paration aux urgences, cartes d\u2019urgence, ressources sur l\u2019injection, soutien et mat\u00e9riel \u00e9ducatif.',
        cta: 'Consulter les ressources de la NADF',
        url: 'https://www.nadf.us/patient-resources',
      },
      {
        org: 'The Canadian Addison Society / La Soci\u00e9t\u00e9 canadienne d\u2019Addison',
        region: 'Canada \u00b7 anglais et fran\u00e7ais',
        desc:
          'Ressources canadiennes comprenant des documents d\u2019urgence, des cartes d\u2019information m\u00e9dicale, des ressources pour les voyages et du mat\u00e9riel destin\u00e9 aux patients et aux intervenants d\u2019urgence.',
        cta: 'Consulter les ressources de la Soci\u00e9t\u00e9 canadienne d\u2019Addison',
        url: 'https://addisonsociety.ca/resources/',
      },
    ],
  },
};

/**
 * Non-commercial section: external adrenal-insufficiency patient organizations.
 * No shop CTAs, no prices, no product mentions inside cards.
 */
export const AdrenalInsufficiencyResources = ({ compact = false }) => {
  const { language } = useLanguage();
  const c = language === 'fr' ? CONTENT.fr : CONTENT.en;

  return (
    <section
      data-testid="external-adrenal-resources"
      aria-labelledby="external-adrenal-resources-title"
      className="bg-white"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-t border-neutral-200 pt-10">
          <div className="flex items-start gap-3 mb-4">
            <BookOpen className="w-6 h-6 text-[#1a1a1a] flex-shrink-0 mt-1" aria-hidden="true" />
            <h2
              id="external-adrenal-resources-title"
              className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight text-[#1a1a1a]"
            >
              {c.title}
            </h2>
          </div>
          <p className="font-manrope text-neutral-600 leading-relaxed mb-8 pl-9">
            {c.intro}
          </p>

          <div className={`grid gap-6 ${compact ? '' : 'md:grid-cols-2'}`}>
            {c.cards.map((card) => (
              <article
                key={card.url}
                className="bg-neutral-50 rounded-2xl border border-neutral-200 p-6"
              >
                <h3 className="font-outfit font-bold text-lg text-[#1a1a1a] mb-1 leading-snug">
                  {card.org}
                </h3>
                <p className="font-manrope text-xs text-neutral-500 uppercase tracking-wider mb-3">
                  {card.region}
                </p>
                <p className="font-manrope text-sm text-neutral-700 leading-relaxed mb-5">
                  {card.desc}
                </p>
                <a
                  href={card.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${card.cta} \u2014 ${c.externalLabel}`}
                  className="inline-flex items-center gap-2 font-manrope font-semibold text-sm text-[#1a1a1a] hover:text-[#E31B23] transition-colors border-b border-neutral-300 hover:border-[#E31B23] pb-0.5"
                  data-testid={`external-resource-link-${new URL(card.url).hostname.replace(/\./g, '-')}`}
                >
                  <span>{card.cta}</span>
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </a>
              </article>
            ))}
          </div>

          <p className="font-manrope text-xs text-neutral-500 leading-relaxed mt-8 pl-0 sm:pl-9">
            {c.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
};

export default AdrenalInsufficiencyResources;
